// Softphone — floating workspace widget with the 12 call controls.
//
// Anchored bottom-right of the workspace shell. Replaces the old Dialer FAB.
// Shows one panel with: dial pad → active call view → wrap-up screen.
//
// 12 controls covered:
//   Hold/Unhold, Mute/Unmute, Recording toggle, DTMF keypad, Voicemail drop,
//   Blind transfer, Attended transfer (start+complete), Conference (3-way add),
//   Add line / Switch line, Hangup, Wrap-up + disposition.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Phone, PhoneCall, PhoneOff, PhoneIncoming, MessageCircle, Mic, MicOff, Pause, Play, Disc, Circle,
  Users2, ArrowRightLeft, Hash, Voicemail, X, ChevronUp, ChevronDown, Loader2,
  CheckCircle2, Plus, ArrowLeftRight, Wifi, WifiOff, Globe, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSoftphone } from "@/contexts/SoftphoneContext";
import { useSipEngine } from "@/hooks/useSipEngine";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { playCallLandedBeep } from "@/lib/callBeep";
import { useConnection } from "@/contexts/ConnectionContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { popScreenOnWorkspace } from "@/lib/app-utils";
import CallTransferControls from "@/components/CallTransferControls";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const DTMF_KEYS = ["1","2","3","4","5","6","7","8","9","*","0","#"];

function fmtTimer(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s/60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2,"0")}`;
}

function CallTimer({ since }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  if (!since) return <span className="font-mono">0:00</span>;
  const t = (now - new Date(since).getTime())/1000;
  return <span className="font-mono">{fmtTimer(t)}</span>;
}

export default function Softphone({ channelMode = "on_hook" } = {}) {
  const sp = useSoftphone();
  const sip = useSipEngine();
  const nav = useNavigate();
  const { flags: featureFlags } = useFeatureFlags();
  const manualDialEnabled = featureFlags?.allow_manual_dial !== false;
  const waCallingEnabled = featureFlags?.wa_calling_enabled === true;
  const beepEnabledRef = useRef(true);
  beepEnabledRef.current = featureFlags?.call_landed_beep !== false;
  useEffect(() => {
    const onLanded = (e) => {
      if (e.detail?.landed && beepEnabledRef.current) playCallLandedBeep();
    };
    window.addEventListener("dialer:bridged", onLanded);
    return () => window.removeEventListener("dialer:bridged", onLanded);
  }, []);
  const { online, reportHealth } = useConnection();
  const [open, setOpen] = useState(false);
  const [pad, setPad] = useState("");
  const [activeLine, setActiveLine] = useState(null);    // session id currently focused
  const [showDtmf, setShowDtmf] = useState(false);
  const [xferOpen, setXferOpen] = useState(false);
  const [confOpen, setConfOpen] = useState(false);
  const [vmOpen, setVmOpen] = useState(false);
  const [wrapOpen, setWrapOpen] = useState(false);
  const [conferenceActive, setConferenceActive] = useState(false);
  // One transfer/conference per call — disables both buttons once either is used.
  const [transferConfUsed, setTransferConfUsed] = useState(false);
  const [transferDialog, setTransferDialog] = useState(null);  // null | "transfer" | "conference"
  const [pendingCdr, setPendingCdr] = useState(null);
  const [dispos, setDispos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [busy, setBusy] = useState(false);
  const [wrapupPolicy, setWrapupPolicy] = useState({ enabled: false, seconds: 0, code_id: "", code_name: "", code_disposition_id: "" });
  const [customerDisconnected, setCustomerDisconnected] = useState(false);
  // Track the live sip session we last saw so we can detect remote-bye.
  const lastSipSessionRef = useRef(null);
  // Set when Agent A transfers a call away (agent-to-agent). The live leg will
  // end a beat later (customer redirected) — we must NOT pop a dispose/hangup
  // bar because the backend already auto-dispositioned A as "Transferred".
  const transferNoWrapRef = useRef(false);
  // Set when the agent clicks the hangup button — if the session ends
  // within 1.5s of that click, it's agent-initiated (not a customer bye).
  const agentHangupAtRef = useRef(0);
  // Slice 16.3 — manual dial uses the ONE campaign the agent selected in the
  // workspace header (not a softphone picker). We just read it for display +
  // client-side gating; the backend enforces it on /me/manual-call.
  const [activeCampaign, setActiveCampaign] = useState(null); // {id,name,record}
  const manualCdrMetaRef = useRef(null); // {campaign_id, campaign_name} for the live call's CDR

  async function loadActiveCampaign() {
    try {
      const r = await api.get("/me/manual-campaign");
      setActiveCampaign(r.data.selected_id
        ? { id: r.data.selected_id, name: r.data.selected_name, record: r.data.record_calls }
        : null);
    } catch { /* non-blocking */ }
  }
  useEffect(() => {
    loadActiveCampaign();
    const onChanged = () => loadActiveCampaign();
    window.addEventListener("c7:campaign-changed", onChanged);
    // Slice 16.3 #3 — dialing from a CRM lead routes through the SAME manual
    // flow (so it's gated, labeled "Manual", and recorded under the campaign).
    const onDialNumber = (e) => {
      const num = e?.detail?.phone || e?.detail?.number;
      if (num) { setPad(String(num)); onCall(String(num)); }
    };
    window.addEventListener("c7:dial-number", onDialNumber);
    return () => {
      window.removeEventListener("c7:campaign-changed", onChanged);
      window.removeEventListener("c7:dial-number", onDialNumber);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaign?.id, sip.mode, sip.registered]);

  // Load active wrap-up policy whenever campaigns change (or on mount).
  useEffect(() => {
    let alive = true;
    api.get("/me/wrapup-policy")
      .then((r) => { if (alive) setWrapupPolicy(r.data || { enabled: false }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [channelMode]);  // re-fetch when campaign mode flips

  // Slice 3.5 — Off-hook auto-park. When the workspace resolves the agent
  // into `off_hook` mode (via campaign join), park a persistent SIP leg
  // into the agent's personal ConfBridge room (`*8{ext}`). The predictive
  // dialer (routes/dialer.py) then bridges customer legs INTO the same
  // room — zero ring latency on the agent side.
  //
  // Guarded by:
  //   * sip.mode === "live"  (no-op in simulator dev)
  //   * sip.registered === true  (REGISTER must have succeeded first)
  //   * sip.creds.extension exists
  useEffect(() => {
    if (sip.mode !== "live" || !sip.registered || !sip.creds?.extension) return;
    if (channelMode === "off_hook" && !sip.offHookActive) {
      sip.inviteOffHook(`*8${sip.creds.extension}`);
    } else if (channelMode !== "off_hook" && sip.offHookActive) {
      sip.terminateOffHook();
    }
  }, [channelMode, sip.mode, sip.registered, sip.creds?.extension, sip.offHookActive]);

  // Slice 3.5l — Softphone heartbeat. While the panel is mounted we ping the
  // backend every 5s (watchdog liveness + dialer parking). We ALSO fold in two
  // telephony-health signals so the agent is alerted even when only Asterisk is
  // down (backend healthy): the backend's `asterisk_ok` flag and the local SIP
  // registration state. `sipHealthy()` only reports a problem AFTER the SIP
  // endpoint has registered at least once (so the initial connect isn't flagged).
  const sipWasRegisteredRef = useRef(false);
  const lastBackendRef = useRef({ backendOk: true, asteriskOk: undefined });
  const sipHealthy = () => {
    if (sip.mode !== "live") return undefined;          // n/a in simulator
    if (!sipWasRegisteredRef.current) return undefined; // not up yet → don't flag
    return !!(sip.registered && sip.transportConnected);
  };
  useEffect(() => {
    let stopped = false;
    async function ping() {
      try {
        const r = await api.post("/me/heartbeat", { channel_mode: channelMode });
        lastBackendRef.current = { backendOk: true, asteriskOk: r.data?.asterisk_ok };
        reportHealth({ backendOk: true, asteriskOk: r.data?.asterisk_ok, sipOk: sipHealthy() });
      } catch (e) {
        // A 401 is a genuine session end (handled by the api interceptor →
        // /session-expired) — don't treat it as a transient outage. Only
        // network errors / 5xx (no or >=500 response) count as "backend down".
        const status = e?.response?.status;
        if (status === 401) return;
        lastBackendRef.current = { backendOk: false, asteriskOk: undefined };
        reportHealth({ backendOk: false });
      }
    }
    ping();
    const tm = setInterval(() => { if (!stopped) ping(); }, 5000);
    return () => { stopped = true; clearInterval(tm); };
  }, [channelMode, reportHealth]);

  // React quickly (don't wait up to 5s) when the SIP registration drops/returns
  // — this is the fastest signal that Asterisk went away for THIS agent. Re-uses
  // the last known backend/asterisk health so the combined verdict stays correct.
  useEffect(() => {
    if (sip.mode !== "live") return;
    if (sip.registered) sipWasRegisteredRef.current = true;
    const { backendOk, asteriskOk } = lastBackendRef.current;
    reportHealth({ backendOk, asteriskOk, sipOk: sipHealthy() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sip.registered, sip.transportConnected, sip.mode]);

  // Auto-recovery is intentionally NOT attempted in place: a browser softphone
  // cannot reliably re-create its SIP registration + off-hook ConfBridge channel
  // after a real disconnect. Instead, once connectivity returns the workspace
  // asks the agent to log out and back in (a fresh page load rebuilds the SIP
  // stack and re-parks cleanly) — see the "Session disconnected" modal in
  // Layout.jsx, driven by ConnectionContext.sessionInterrupted.

  // Auto-pop the panel on any active session (simulator OR live)
  useEffect(() => {
    if (sp.sessions.length > 0 || sip.session) setOpen(true);
    if (sp.sessions.length === 0 && !sip.session) { setActiveLine(null); setShowDtmf(false); }
  }, [sp.sessions.length, sip.session?.id]);

  // Broadcast a unified call-activity signal (ringing / in-call) so the
  // workspace sidebar can lock navigation while a call is ringing or live —
  // agents may only navigate when paused/offline (and not on a call).
  useEffect(() => {
    const simRinging = sp.sessions.some((s) => s.state === "ringing");
    const simInCall = sp.sessions.some(
      (s) => ["dialing", "ringing", "talking", "held"].includes(s.state));
    const sipState = sip.session?.state;
    const sipRinging = !!sip.incomingInvitation
      || sipState === "ringing" || sipState === "initial";
    const sipInCall = !!sip.session
      && ["initial", "ringing", "talking", "ending"].includes(sipState);
    // The call_id here MUST match what the manual CDR/recording uses
    // (`sip-${session.id}`) so screen recordings link to their call in QM.
    const activeCallId = sip.session?.id ? `sip-${sip.session.id}` : null;
    try {
      window.dispatchEvent(new CustomEvent("c7:call-activity", {
        detail: { ringing: simRinging || sipRinging, inCall: simInCall || sipInCall, call_id: activeCallId },
      }));
    } catch { /* ignore */ }
  }, [sp.sessions, sip.session?.id, sip.session?.state, sip.incomingInvitation?.id]);

  // Pop inbound WebRTC ring as toast
  useEffect(() => {
    if (sip.incomingInvitation && sip.session?.direction === "inbound") {
      toast.message("Incoming WebRTC call", { description: sip.session.peer_name || sip.session.peer });
      setOpen(true);
    }
  }, [sip.incomingInvitation?.id]);

  // Track focused line (default: first non-held, else first)
  useEffect(() => {
    if (!sp.sessions.length) return;
    if (activeLine && sp.sessions.find((s) => s.id === activeLine)) return;
    const ringing = sp.sessions.find((s) => s.state === "ringing" && s.direction === "inbound");
    const talking = sp.sessions.find((s) => s.state === "talking");
    setActiveLine((ringing || talking || sp.sessions[0]).id);
  }, [sp.sessions, activeLine]);

  // Listen for the workspace's red hangup button (ConversationView).
  // Two distinct paths:
  //   1. There's a predictive/auto call bridged via ARI → call the
  //      backend /api/me/active-call/hangup endpoint which hangs up the
  //      CUSTOMER's leg (NOT the agent's ConfBridge park) so the agent
  //      stays seated for the next predictive dial. The endpoint returns
  //      a synthetic CDR which we feed to the wrap-up dialog.
  //   2. It's a manual sip.js call → stamp the agentHangup ref and call
  //      sip.terminate() — the session-state effect auto-opens wrap-up.
  useEffect(() => {
    async function onWsHangup() {
      // The agent-page red button is the PRIMARY hangup for EVERY call type
      // (inbound + outbound) and must ALWAYS end the call AND open the
      // disposition dialog — exactly like the auto-dial hangup.
      //
      // STEP 0 — ALWAYS tear down the agent's own WebRTC leg FIRST.
      // sip.terminate() is a stable callback that reads the live session via
      // an internal ref (NOT a stale React-state closure), and is a no-op when
      // there's no session. Sending this BYE is the PROVEN teardown: it
      // collapses the agent's ConfBridge leg which drops the customer too
      // (verified on the live Linode server). The workspace red button relied
      // ONLY on the backend AMI hangup before — which can no-op on a
      // multi-worker deploy — leaving the WebRTC + customer connected. The
      // softphone-widget hangup always worked precisely because it called
      // sip.terminate() directly; we now do the same here for ALL surfaces.
      agentHangupAtRef.current = Date.now();
      try { sip.terminate(); } catch {}
      // Case A — the call already ended (e.g. customer disconnected → the
      // dispose bar is showing a pendingCdr). The agent clicking hangup now
      // should just OPEN the wrap-up dialog. (Checked AFTER terminate so a
      // live leg is never left up.)
      if (pendingCdrRef.current) {
        setWrapOpen(true);
        return;
      }
      // Simulator session (preview/testing path — no sip.js leg and no
      // backend dialer/queue row). End it via the simulator hangup; the
      // `sp.recentCdr` effect then surfaces the post-call dispose bar so the
      // agent can pick a disposition exactly like a live call.
      const simCur = sp.sessions.find((s) => ["talking", "held", "ringing"].includes(s.state));
      if (simCur) {
        setOpen(true);
        try { await sp.hangup(simCur.id); } catch {}
        return;
      }
      // 2) Clean up the SERVER-SIDE peer leg:
      //    - predictive/power/progressive outbound → ARI hangup of the
      //      customer channel (off-hook agents have no sip.session) and a
      //      synthetic CDR to open wrap-up immediately.
      //    - inbound ACD (queue) → AMI hangup of the caller channel; the
      //      AgentComplete event then fires dialer.ended which auto-opens
      //      the dialog (agent-initiated → see onCustomerHangup below).
      try {
        const r = await api.post("/me/active-call/hangup");
        const d = r.data || {};
        if (d.is_inbound_queue) {
          if (!d.ok && d.detail) {
            try { toast.warning(`Hangup: ${d.detail}`); } catch {}
          }
          return;
        }
        if (d.is_dialer_call) {
          const cdr = {
            call_id:           d.call_id,
            peer:              d.peer,
            peer_name:         d.peer_name,
            direction:         d.direction || "outbound",
            started_at:        d.started_at,
            answered_at:       d.answered_at,
            ended_at:          d.ended_at,
            duration_seconds:  d.duration_seconds || 0,
            billable_seconds:  d.billable_seconds || 0,
            campaign_id:       d.campaign_id,
            lead_id:           d.lead_id,
            is_live:           true,
          };
          setPendingCdr(cdr);
          setCustomerDisconnected(false);
          setWrapOpen(true);
          if (!d.ok && d.ari_error) {
            try { toast.warning(`Customer leg hangup: ${d.ari_error}`); } catch {}
          }
          return;
        }
      } catch (e) {
        // 404 = no server-side dialer/queue call (e.g. a plain WebRTC call
        // already ended by sip.terminate above — the sip session-end effect
        // opens the dialog). Anything else → surface it.
        if (e?.response?.status !== 404) {
          try { toast.error(formatErr(e)); } catch {}
        }
      }
    }
    window.addEventListener("workspace:hangup", onWsHangup);
    return () => window.removeEventListener("workspace:hangup", onWsHangup);
  }, [sip.session, sip.terminate, sp.sessions, sp.hangup]);

  // Slice 3.5i (round 4) — customer-side hangup auto-wrap-up.
  // Backend ChannelLeftBridge handler broadcasts a `dialer.ended` WS
  // event when the carrier drops the customer leg. useSoftphone.js
  // re-dispatches it as `dialer:customer-hangup` with a pre-built CDR
  // detail. We open the wrap-up dialog with customerDisconnected=true
  // so the agent CAN ALWAYS disposition the call (previously the
  // workspace card just cleared and the disposition was lost).
  // Slice 3.5l — On `dialer:customer-hangup` we DO NOT auto-open the
  // wrap-up dialog. The agent reviews the CRM card first and clicks
  // "Dispose" (post-call-dispose-bar) when ready. If `wrapupPolicy.enabled`,
  // a parent-level timer auto-saves the call with the configured
  // fallback disposition at policy.seconds (no dialog flicker).
  const autoWrapTimerRef = useRef(null);
  const pendingCdrRef = useRef(null);
  useEffect(() => { pendingCdrRef.current = pendingCdr; }, [pendingCdr]);

  // Issue #3 — mirror the disposition + agent notes into the customer's
  // "Notes & Comment History". Best-effort: resolves the contact by the call's
  // peer phone and appends a tagged note. Works for every call type because
  // every disposition (manual/predictive/inbound/auto) funnels through here.
  async function addDispositionComment(cdr, dispoId, notes) {
    try {
      const phone = (cdr?.peer || "").trim();
      if (!phone) return;
      const d = dispos.find((x) => (x.id || x.code || x.name) === dispoId);
      const dispoName = d?.name || d?.code || cdr?.disposition_name || dispoId || "—";
      const dirLabel = cdr.direction === "inbound" ? "Inbound"
        : (cdr.campaign_id && cdr.lead_id && !cdr.is_inbound_dialer) ? "Outbound · Predictive"
        : "Outbound · Manual";
      const dur = fmtTimer(cdr.billable_seconds || cdr.duration_seconds || 0);
      const tag = `[Disposition: ${dispoName} · ${dirLabel} · ${dur}]`;
      const body = notes && notes.trim() ? `${tag}\n${notes.trim()}` : tag;
      const r = await api.get("/contacts/by-phone", { params: { phone } });
      const cid = r.data?.id;
      if (cid) await api.post(`/contacts/${cid}/comments`, { body });
    } catch { /* best-effort — never block wrap-up */ }
  }

  // Issue #2 — expose a global "call busy" flag so the Layout can block
  // sign-out while a call is live OR a disposition is still pending. Also
  // honour a "workspace:open-wrap" request to surface the wrap-up dialog.
  useEffect(() => {
    const callLive = sp.sessions.some((s) =>
      ["dialing", "ringing", "talking", "held"].includes(s.state)) || !!sip.session;
    const wrapPending = !!pendingCdr || wrapOpen || customerDisconnected;
    window.__c7_callBusy = callLive || wrapPending;
    window.__c7_wrapPending = wrapPending;
    return () => { /* keep last value until next render */ };
  }, [sp.sessions, sip.session, pendingCdr, wrapOpen, customerDisconnected]);

  useEffect(() => {
    function onOpenWrap() {
      if (pendingCdrRef.current) { setOpen(true); setWrapOpen(true); }
    }
    window.addEventListener("workspace:open-wrap", onOpenWrap);
    return () => window.removeEventListener("workspace:open-wrap", onOpenWrap);
  }, []);

  async function autoWrapPendingCdr() {
    const cdr = pendingCdrRef.current;
    if (!cdr) return;
    if (!dispos.length) { try { await ensureDispos(); } catch {} }
    const fallback = wrapupPolicy?.code_disposition_id || dispos[0]?.id;
    if (!fallback) return;
    const tag = wrapupPolicy?.code_name || "default";
    const notes = `[Auto-wrapped via ${tag}]`;
    try {
      if (cdr.is_inbound_dialer) {
        await api.post("/me/inbound-cdr/disposition", {
          cdr_id: cdr.cdr_id, disposition_id: fallback, notes,
        });
      } else if (typeof cdr.lead_id === "string" && cdr.lead_id.startsWith("cb:")) {
        await api.post(`/me/callbacks/${cdr.lead_id.slice(3)}/complete`, {
          disposition_id: fallback, notes,
          duration_seconds: cdr.billable_seconds || cdr.duration_seconds || 0,
          answered: true, call_id: cdr.call_id,
        });
      } else if (cdr.is_live) {
        await api.post("/cdrs/live", {
          call_id: cdr.call_id, direction: cdr.direction,
          peer: cdr.peer, peer_name: cdr.peer_name,
          started_at: cdr.started_at, answered_at: cdr.answered_at,
          ended_at: cdr.ended_at || new Date().toISOString(),
          disposition_id: fallback, wrapup_notes: notes,
          campaign_id: cdr.campaign_id, hangup_side: cdr.hangup_side,
        });
      } else if (!(cdr.campaign_id && cdr.lead_id)) {
        try { await sp.wrapup(cdr.call_id, fallback, notes); } catch {}
      }
      // Dialer/predictive call → also update the lead row + inflight so
      // the auto-wrapped lead reflects the fallback disposition.
      if (cdr.campaign_id && cdr.lead_id && !cdr.is_inbound_dialer) {
        try {
          await api.post(`/dialer/campaigns/${cdr.campaign_id}/disposition`, {
            lead_id: cdr.lead_id, outcome: "answered",
            disposition_id: fallback, notes,
            duration_seconds: cdr.billable_seconds || cdr.duration_seconds || 0,
          });
        } catch {}
      }
    } catch {}
    setPendingCdr(null);
    setCustomerDisconnected(false);
    addDispositionComment(cdr, fallback, notes);
    try { window.dispatchEvent(new CustomEvent("c7:persist-crm")); } catch {}
    try { window.dispatchEvent(new CustomEvent("dialer:ended", { detail: { source: "auto-wrap" } })); } catch {}
    try { toast.success(`Auto-wrapped as ${tag}`); } catch {}
  }

  useEffect(() => {
    function onCustomerHangup(e) {
      const cdr = e.detail?.cdr;
      if (!cdr) return;
      // The SIP remote-bye path already set pendingCdr + banner + toast locally
      // (it dispatched this event only to notify the workspace badge). Skip
      // re-handling here to avoid a duplicate toast / wrap-up prompt.
      if (e.detail?.source === "sip-remote") return;
      setPendingCdr(cdr);
      // If the agent just clicked a hangup button (agent-page red button or
      // the softphone widget) within the last few seconds, this end is
      // AGENT-INITIATED → open the wrap-up dialog immediately (same as the
      // auto-dial hangup). Otherwise the CUSTOMER disconnected → show the
      // dispose bar + toast and let the agent review the CRM card first.
      const agentInitiated = (Date.now() - agentHangupAtRef.current) < 6000;
      if (agentInitiated) {
        setCustomerDisconnected(false);
        setWrapOpen(true);
        return;
      }
      setCustomerDisconnected(true);  // banner: "Customer disconnected — wrap up when ready"
      try { toast.warning("Customer disconnected", {
        description: "Click Dispose when you're ready to wrap up."
      }); } catch {}
      // Auto-wrap timer ONLY when policy is enabled
      if (autoWrapTimerRef.current) { clearTimeout(autoWrapTimerRef.current); autoWrapTimerRef.current = null; }
      const secs = parseInt(wrapupPolicy?.seconds || 0, 10);
      if (wrapupPolicy?.enabled && secs > 0) {
        autoWrapTimerRef.current = setTimeout(() => {
          autoWrapTimerRef.current = null;
          autoWrapPendingCdr();
        }, secs * 1000);
      }
    }
    window.addEventListener("dialer:customer-hangup", onCustomerHangup);
    return () => {
      window.removeEventListener("dialer:customer-hangup", onCustomerHangup);
      if (autoWrapTimerRef.current) { clearTimeout(autoWrapTimerRef.current); autoWrapTimerRef.current = null; }
    };
  }, [wrapupPolicy?.enabled, wrapupPolicy?.seconds, wrapupPolicy?.code_disposition_id, wrapupPolicy?.code_name]);

  // After hangup → set pendingCdr but DO NOT auto-open the wrap-up
  // dialog. Agent reviews the CRM info first and clicks the new
  // "Dispose" button (post-call-dispose-bar) when ready.
  useEffect(() => {
    if (sp.recentCdr && !sp.sessions.length) {
      setPendingCdr(sp.recentCdr);
      setCustomerDisconnected(false);  // simulator path — agent-driven
    }
  }, [sp.recentCdr, sp.sessions.length]);

  // Track sip.js session lifecycle. When the session terminates (live
  // WebRTC call), pop the wrap-up dialog with a synthetic CDR. Detect
  // remote-bye (customer disconnected) vs local-bye (agent ended).
  // Also captures started_at / answered_at / ended_at timestamps so the
  // CDR row has accurate duration and AHT data.
  useEffect(() => {
    const cur = sip.session;
    const prev = lastSipSessionRef.current;
    if (cur && cur.id) {
      const same = prev && prev.id === cur.id;
      // First time we see this session — stamp started_at.
      const startedAt = same
        ? prev.started_at
        : (cur.started_at || new Date().toISOString());
      // First time we see state=talking — stamp answered_at.
      const answeredAt = (prev && prev.answered_at)
        ? prev.answered_at
        : (cur.state === "talking" ? new Date().toISOString() : null);
      // Accumulate hold time across every hold/unhold toggle in this call.
      let holdAccumMs = same ? (prev.hold_accum_ms || 0) : 0;
      let holdStartedAt = same ? (prev.hold_started_at || null) : null;
      const isHeld = cur.state === "held" || cur.held;
      if (isHeld && !holdStartedAt) {
        holdStartedAt = Date.now();
      } else if (!isHeld && holdStartedAt) {
        holdAccumMs += Date.now() - holdStartedAt;
        holdStartedAt = null;
      }
      lastSipSessionRef.current = {
        ...cur, started_at: startedAt, answered_at: answeredAt,
        hold_accum_ms: holdAccumMs, hold_started_at: holdStartedAt,
      };
    }
    // Detect "ended" → pop wrap-up
    if (prev && (!cur || cur.state === "ended" || cur.state === "ending") && !pendingCdr) {
      // Agent A transferred this call away — the leg ends because the customer
      // was redirected, NOT because the call needs disposing. Suppress the
      // synthetic CDR + dispose/hangup bar entirely (backend already stamped
      // "Transferred").
      if (transferNoWrapRef.current) {
        lastSipSessionRef.current = null;
        return;
      }
      // Agent transferred this call away → they already left + were
      // auto-dispositioned "Transferred". Quietly tear down with NO dispose
      // bar / wrap dialog / synthetic CDR (the backend owns A's CDR).
      if (transferNoWrapRef.current) {
        transferNoWrapRef.current = false;
        lastSipSessionRef.current = null;
        manualCdrMetaRef.current = null;
        manualActiveRef.current = false;
        manualEndPendingRef.current = false;
        setCustomerDisconnected(false);
        try { window.dispatchEvent(new CustomEvent("dialer:ended", { detail: { source: "transfer" } })); } catch {}
        return;
      }
      // Inbound ACD (queue) calls are owned by the server-side AMI listener
      // (it writes the CDR at AgentComplete + broadcasts dialer.ended → the
      // `dialer:customer-hangup` dispose bar with the real cdr_id). Building a
      // 2nd client-side CDR here would DUPLICATE it and route the disposition
      // to the wrong endpoint, so skip the sip.js synthetic-CDR path for
      // inbound and let the AMI pipeline drive the wrap-up.
      if (prev.direction === "inbound") {
        lastSipSessionRef.current = null;
        return;
      }
      const endedAt = new Date().toISOString();
      const meta = manualCdrMetaRef.current || {};
      // Derive granular timings for the report columns.
      const durationSeconds = prev.started_at
        ? Math.max(0, Math.floor((new Date(endedAt) - new Date(prev.started_at)) / 1000)) : 0;
      const billableSeconds = prev.answered_at
        ? Math.max(0, Math.floor((new Date(endedAt) - new Date(prev.answered_at)) / 1000)) : 0;
      // Agent-side ring time = dial/ring → answered (or → ended if never answered).
      const ringSeconds = prev.started_at
        ? Math.max(0, Math.floor(((prev.answered_at ? new Date(prev.answered_at) : new Date(endedAt)) - new Date(prev.started_at)) / 1000))
        : 0;
      // Total hold time (include a still-open hold segment at hangup).
      let holdMs = prev.hold_accum_ms || 0;
      if (prev.hold_started_at) holdMs += Date.now() - prev.hold_started_at;
      const holdSeconds = Math.floor(holdMs / 1000);
      const talkSeconds = Math.max(0, billableSeconds - holdSeconds);
      const agentClickedRecently = (Date.now() - agentHangupAtRef.current) < 1500;
      const remoteBye = !agentClickedRecently;
      // Remote end before answer = carrier/network rejection; after = customer.
      const hangupSide = !remoteBye ? "agent" : (prev.answered_at ? "customer" : "carrier");
      const synthCdr = {
        call_id:     `sip-${prev.id}`,
        peer:        prev.peer,
        peer_name:   prev.peer_name || prev.peer,
        direction:   prev.direction,
        campaign_id: meta.campaign_id || null,
        started_at:  prev.started_at || endedAt,
        answered_at: prev.answered_at,
        ended_at:    endedAt,
        duration_seconds:  durationSeconds,
        billable_seconds:  billableSeconds,
        ring_seconds:      ringSeconds,
        hold_seconds:      holdSeconds,
        talk_seconds:      talkSeconds,
        hangup_side:       hangupSide,
        is_live: true,
      };
      setPendingCdr(synthCdr);
      // Issue #1 — guarantee a CDR (and thus a recordings row) on BOTH
      // agent-hangup and customer-hangup: persist immediately with no
      // disposition. The wrap-up save re-posts (idempotent on call_id) to add
      // the disposition. Without this, agent-initiated hangups that dismiss
      // the dialog left manual calls with NO log at all.
      try {
        api.post("/cdrs/live", {
          call_id: synthCdr.call_id, direction: synthCdr.direction,
          peer: synthCdr.peer, peer_name: synthCdr.peer_name,
          started_at: synthCdr.started_at, answered_at: synthCdr.answered_at,
          ended_at: synthCdr.ended_at, campaign_id: synthCdr.campaign_id,
          ring_seconds: synthCdr.ring_seconds, hold_seconds: synthCdr.hold_seconds,
          talk_seconds: synthCdr.talk_seconds, hangup_side: synthCdr.hangup_side,
        }).catch(() => {});
      } catch {}
      manualCdrMetaRef.current = null;
      setCustomerDisconnected(remoteBye);
      if (remoteBye) {
        // Customer-initiated disconnect — show toast + Dispose button
        // bar, but DO NOT auto-open wrap-up so the agent has time to
        // finish CRM notes before being prompted for a disposition.
        try { toast.warning("Customer disconnected — wrap up when ready"); } catch {}
        // Notify the workspace so the live-call badge flips green → red.
        // `source: sip-remote` tells our OWN onCustomerHangup listener to skip
        // re-handling (we've already set pendingCdr / banner / toast here).
        try {
          window.dispatchEvent(new CustomEvent("dialer:customer-hangup", {
            detail: { cdr: synthCdr, ended_by: "customer_hangup", source: "sip-remote" },
          }));
        } catch {}
      } else {
        // Agent clicked the red hangup button — open wrap-up dialog
        // IMMEDIATELY so they can pick a disposition and move on.
        setWrapOpen(true);
      }
      lastSipSessionRef.current = null;
    }
  }, [sip.session?.id, sip.session?.state]);

  // Lazy-load dispositions + audio prompts when first needed
  async function ensureDispos() {
    if (dispos.length) return;
    try {
      const r = await api.get("/me/dispositions");
      setDispos(r.data || []);
    } catch { /* silent */ }
  }
  async function ensureAudios() {
    if (audios.length) return;
    try {
      const r = await api.get("/me/audio-prompts");
      setAudios(r.data || []);
    } catch { /* silent */ }
  }

  const active = sp.sessions.find((s) => s.id === activeLine) || null;
  const otherLine = sp.sessions.find((s) => s.id !== activeLine) || null;

  // Slice 15.1 — manual WebRTC call lifecycle → backend state capture.
  // started (on dial) → agent shows "In Call (manual)" + auto-switch to
  // Manual Dial; ended (sip session terminates) → Wrap; wrapped (wrap dialog
  // closes) → back to Manual Dial, ready to dial again.
  const manualActiveRef = useRef(false);
  const prevSipSessionRef = useRef(null);
  const prevWrapOpenRef = useRef(false);
  const wrapOpenedAtRef = useRef(0);
  // When the CUSTOMER hangs up a manual call first, we must NOT move the
  // backend to Wrap yet — the agent is in a DEAD call until they click
  // "Hangup & dispose". So we defer the manual-call `ended` event (which sets
  // agent_state=wrap + clears the dead marker) until the wrap-up opens.
  const manualEndPendingRef = useRef(false);

  useEffect(() => {
    const had = prevSipSessionRef.current;
    const now = sip.session?.id || null;
    prevSipSessionRef.current = now;
    if (had && !now && manualActiveRef.current) {
      // Transferred away → backend already moved A to ready + cleared
      // manual_call; don't post an "ended" (which would force Wrap).
      if (transferNoWrapRef.current) { manualActiveRef.current = false; return; }
      const agentClicked = (Date.now() - agentHangupAtRef.current) < 1500;
      if (agentClicked) {
        // Agent clicked the red Hangup button → go to Wrap immediately.
        api.post("/me/manual-call", { event: "ended" }).catch(() => {});
      } else {
        // Customer disconnected first → stay on the (dead) call; the workspace
        // reports dead_call to the floor. Defer the Wrap transition until the
        // agent clicks Dispose (opens the wrap-up dialog).
        manualEndPendingRef.current = true;
      }
    }
  }, [sip.session?.id]);

  // Customer-disconnect case: the moment the agent opens the wrap-up dialog
  // (clicks Dispose), transition the backend from dead-call → Wrap.
  useEffect(() => {
    if (wrapOpen && manualActiveRef.current && manualEndPendingRef.current) {
      manualEndPendingRef.current = false;
      api.post("/me/manual-call", { event: "ended" }).catch(() => {});
    }
  }, [wrapOpen]);

  // Stamp when the wrap-up dialog opened so we can report wrap-up duration.
  useEffect(() => { if (wrapOpen) wrapOpenedAtRef.current = Date.now(); }, [wrapOpen]);

  useEffect(() => {
    if (prevWrapOpenRef.current && !wrapOpen && manualActiveRef.current) {
      manualActiveRef.current = false;
      manualEndPendingRef.current = false;
      api.post("/me/manual-call", { event: "wrapped" }).catch(() => {});
    }
    prevWrapOpenRef.current = wrapOpen;
  }, [wrapOpen]);

  // ── Live-dashboard call-phase reporter (ALL dial modes) ──────────────
  // The Softphone owns the post-call UI for every mode (manual, predictive,
  // power, auto, inbound), so it is the single source of truth for the phase
  // the supervisor floor must show:
  //   • DEAD  = customer disconnected first AND the agent hasn't opened the
  //             wrap-up yet (customerDisconnected && !wrapOpen).
  //   • WRAP  = the agent clicked Hangup&dispose / Dispose (wrapOpen) → not dead.
  // We report DEAD on/off via /me/call-dead; the floor clears it → shows Wrap.
  const deadReportedRef = useRef(false);
  useEffect(() => {
    const isDead = customerDisconnected && !wrapOpen;
    if (isDead && !deadReportedRef.current) {
      deadReportedRef.current = true;
      api.post("/me/call-dead", { dead: true, call_id: pendingCdr?.call_id || null }).catch(() => {});
    } else if (!isDead && deadReportedRef.current) {
      deadReportedRef.current = false;
      api.post("/me/call-dead", { dead: false }).catch(() => {});
    }
  }, [customerDisconnected, wrapOpen, pendingCdr?.call_id]);

  async function onCall(numberArg) {
    const dialedNumber = (typeof numberArg === "string" && numberArg ? numberArg : pad).trim();
    if (!dialedNumber) return;
    // Gated by the Agent-UI "Allow manual dial" toggle (role / group override).
    if (!manualDialEnabled) {
      toast.error("Manual dialing is disabled for your profile.");
      return;
    }
    // Block dialing while the backend is unreachable — otherwise the call
    // silently fails and the agent can't tell why. The connection banner is
    // already visible; this just gives immediate feedback on the dial attempt.
    if (!online) {
      toast.error("Reconnecting to server — please wait a moment before dialing.");
      return;
    }
    // The SIP dial string must be digits-only (+ optional * / #). The generated
    // [from-internal] outbound pattern `_X.` matches DIGITS only, so dialing a
    // stored E.164 number like "+918075742338" (leading '+') was rejected by
    // Asterisk ("extension not found"). Strip non-dialable chars for the INVITE
    // target while keeping the original number for the CDR + screen-pop so the
    // dropped-call callback still matches the inbound caller-id.
    const dialString = dialedNumber.replace(/[^\d*#]/g, "");
    if (!dialString) return;
    // Slice 16.3 — manual dial requires the agent's header-selected campaign.
    if (sip.mode === "live" && sip.registered && !activeCampaign?.id) {
      toast.error("Select a campaign in the header before dialing.");
      return;
    }
    setBusy(true);
    try {
      if (sip.mode === "live" && sip.registered) {
        // Manual WebRTC dial — gate on the server FIRST (campaign selected +
        // not already on a call). Only invite once the server accepts.
        let started;
        try {
          started = await api.post("/me/manual-call", {
            event: "started", call_id: dialedNumber,
            peer: dialedNumber, peer_name: "",
          });
        } catch (e) {
          toast.error(formatErr(e));   // 400 no-campaign / 409 already-on-call
          return;
        }
        manualCdrMetaRef.current = {
          campaign_id: started?.data?.campaign_id || activeCampaign?.id,
          campaign_name: started?.data?.campaign_name || activeCampaign?.name || "",
        };
        // CRM screen-pop now that the dial is authorized. Open the workspace
        // first if the agent dialed from another page (Call logs / Contacts).
        popScreenOnWorkspace({ phone: dialedNumber, name: "", source: "manual_dial" }, nav);
        manualActiveRef.current = true;
        try { window.dispatchEvent(new CustomEvent("c7:set-presence", { detail: { display: "Manual Dial" } })); } catch {}
        await sip.invite(dialString);
      } else {
        popScreenOnWorkspace({ phone: dialedNumber, name: "", source: "manual_dial" }, nav);
        await sp.originate(dialString);
      }
      setPad("");
    } catch (e) {
      toast.error(e?.message || "Call failed");
    } finally { setBusy(false); }
  }

  async function onSimRing() {
    setBusy(true);
    try { await sp.simulateInbound("+91 99999 12345", "Demo Caller"); }
    finally { setBusy(false); }
  }

  async function onWaCall() {
    const num = (pad || "").replace(/[^\d]/g, "");
    if (!num) { toast.error("Enter a WhatsApp number"); return; }
    setBusy(true);
    try {
      const r = await api.post("/me/wa-call", { wa_id: num });
      const a = r.data?.action;
      if (a === "call") toast.success(r.data?.simulated ? "WhatsApp call placed (simulated)" : "WhatsApp call placed");
      else if (a === "permission_requested") toast.info("Permission request sent — waiting for the customer to allow.");
      else toast(r.data?.detail || "Done");
      setPad("");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }

  async function withBusy(fn) {
    setBusy(true); try { await fn(); } finally { setBusy(false); }
  }

  // Build the active-call context for the transfer/conference backend. Only
  // needed for a live manual WebRTC leg (no server row); inbound/predictive/
  // simulator calls are resolved server-side from their state rows.
  function getCallCtx() {
    const cur = sip.session || lastSipSessionRef.current;
    if (!cur) return null;
    const meta = manualCdrMetaRef.current || {};
    return {
      call_id: `sip-${cur.id}`,
      peer: cur.peer, peer_name: cur.peer_name || cur.peer,
      direction: cur.direction || "outbound",
      campaign_id: meta.campaign_id || activeCampaign?.id || null,
      started_at: cur.started_at, answered_at: cur.answered_at,
    };
  }
  // A transfer/leave/all completed → open the wrap-up dialog (agent disposes).
  function onTransferWrapCdr(cdr) {
    if (!cdr) return;
    agentHangupAtRef.current = Date.now();
    if (sip.session) { try { sip.terminate(); } catch {} }
    setPendingCdr(cdr);
    setCustomerDisconnected(false);
    setWrapOpen(true);
  }
  useEffect(() => {
    const onConfActive = (e) => setConferenceActive(!!e?.detail?.active);
    window.addEventListener("c7:conference-active", onConfActive);
    // Open the transfer/conference dialog AND ensure the panel is mounted so
    // the dialog renders even when the agent hasn't opened the softphone yet.
    const onOpenXfer = () => { setOpen(true); setTransferDialog("transfer"); };
    const onOpenConf = () => { setOpen(true); setTransferDialog("conference"); };
    window.addEventListener("c7:open-transfer", onOpenXfer);
    window.addEventListener("c7:open-conference", onOpenConf);
    // One transfer/conference per call: disable both buttons once either is
    // used; re-enable on a cancelled warm-consult; reset on a new call.
    const onTcUsed = () => setTransferConfUsed(true);
    const onTcReset = () => setTransferConfUsed(false);
    const onNewCall = () => setTransferConfUsed(false);
    window.addEventListener("c7:transfer-conf-used", onTcUsed);
    window.addEventListener("c7:transfer-conf-reset", onTcReset);
    window.addEventListener("dialer:bridged", onNewCall);
    // Only a FULL call-end clears the conference controls. We intentionally do
    // NOT reset on customer-hangup: if customer1 drops mid-conference the agent
    // must keep the split-hangup to disconnect the 3rd party / all lines.
    const onCallGone = () => {
      setConferenceActive(false);
      setTransferConfUsed(false);
      try { window.dispatchEvent(new CustomEvent("c7:conference-active", { detail: { active: false } })); } catch {}
    };
    window.addEventListener("dialer:ended", onCallGone);
    return () => {
      window.removeEventListener("c7:conference-active", onConfActive);
      window.removeEventListener("c7:open-transfer", onOpenXfer);
      window.removeEventListener("c7:open-conference", onOpenConf);
      window.removeEventListener("c7:transfer-conf-used", onTcUsed);
      window.removeEventListener("c7:transfer-conf-reset", onTcReset);
      window.removeEventListener("dialer:bridged", onNewCall);
      window.removeEventListener("dialer:ended", onCallGone);
    };
  }, []);
  // Agent A transferred the call away → quietly tear down the live-call UI
  // (no dispose/hangup bar). The backend already auto-dispositioned A as
  // "Transferred" and returned them to a ready state.
  useEffect(() => {
    function onTransferNoWrap() {
      transferNoWrapRef.current = true;
      // Mark as agent-initiated so the sip-end handler never treats the
      // customer-redirect as a remote bye (which would pop the dispose bar).
      agentHangupAtRef.current = Date.now();
      if (sip.session) { try { sip.terminate(); } catch {} }
      setPendingCdr(null);
      setCustomerDisconnected(false);
      setWrapOpen(false);
      lastSipSessionRef.current = null;
      manualActiveRef.current = false;
      manualEndPendingRef.current = false;
      // Clear the workspace live-call bar + Layout call-busy lock.
      try { window.dispatchEvent(new CustomEvent("dialer:ended", { detail: { source: "transfer" } })); } catch {}
      try { window.dispatchEvent(new CustomEvent("c7:call-activity", { detail: { ringing: false, inCall: false, call_id: null } })); } catch {}
      toast.success("Call transferred — you're ready for the next call");
      // Reset the one-shot flag after the sip-end effect has had a chance to run.
      setTimeout(() => { transferNoWrapRef.current = false; }, 4000);
    }
    window.addEventListener("c7:transfer-no-wrap", onTransferNoWrap);
    return () => window.removeEventListener("c7:transfer-no-wrap", onTransferNoWrap);
  }, [sip.session, sip.terminate]);

  // Mute / Hold driven from the ConversationView control bar (header).
  useEffect(() => {
    function onCtl(e) {
      const a = e?.detail?.action;
      if (sip.session) {
        if (a === "mute") { try { sip.setMuted(!sip.session.muted); } catch {} }
        if (a === "hold") { try { sip.setHeld(!sip.session.held); } catch {} }
        return;
      }
      const cur = sp.sessions.find((s) => ["talking", "held"].includes(s.state));
      if (!cur) return;
      if (a === "mute") { cur.muted ? sp.unmute(cur.id) : sp.mute(cur.id); }
      if (a === "hold") { cur.state === "held" ? sp.unhold(cur.id) : sp.hold(cur.id); }
    }
    window.addEventListener("c7:call-control", onCtl);
    return () => window.removeEventListener("c7:call-control", onCtl);
  }, [sip.session, sp.sessions]);

  // Slice 3.5i (round 5) — auto-open the softphone panel whenever a
  // pendingCdr is queued OR the wrap-up dialog wants to open. The compact
  // pill below unmounts the WrapUpDialog entirely, so any setWrapOpen(true)
  // call would be invisible if the panel was closed (hit on predictive
  // calls where sp.sessions is empty because predictive uses ARI, not
  // sip.js sessions). Forcing open=true keeps the dialog mounted and
  // visible the instant we set it.
  useEffect(() => {
    if (pendingCdr || wrapOpen) setOpen(true);
  }, [pendingCdr, wrapOpen]);

  // Open compact pill if there's no panel and no calls
  if (!open && !sp.sessions.length) {
    return (
      <button
        data-testid="softphone-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-primary/20 grid place-items-center hover:scale-105 active:scale-95 transition-transform z-40"
        title="Open softphone"
      >
        <Phone className="w-5 h-5" />
      </button>
    );
  }

  return (
    <>
      <div data-testid="softphone-panel" className="fixed bottom-6 right-6 w-[360px] z-40">
        <Card className="overflow-hidden border-2 border-primary/20 shadow-2xl">
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b">
            <Phone className="w-4 h-4 text-primary" />
            <div className="text-sm font-display font-bold flex-1">Softphone</div>
            <Badge variant="outline" className="text-[10px] font-mono">ext {sp.extension || sip.creds?.extension || "—"}</Badge>
            {sip.mode === "live" ? (
              sip.registered ? (
                <Badge data-testid="sip-registered" variant="outline" className="text-[10px] text-emerald-700 border-emerald-400 gap-1">
                  <Wifi className="w-3 h-3"/>LIVE
                </Badge>
              ) : (
                <Badge data-testid="sip-unregistered" variant="outline" className="text-[10px] text-red-700 border-red-400 gap-1" title={sip.registerError || "Connecting…"}>
                  <WifiOff className="w-3 h-3"/>{sip.registerError ? "OFFLINE" : "…"}
                </Badge>
              )
            ) : sip.credError ? (
              <Badge data-testid="sip-no-license" variant="outline" className="text-[10px] text-red-700 border-red-400 gap-1" title={sip.credError}>
                <WifiOff className="w-3 h-3"/>NO LINE
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-400">simulator</Badge>
            )}
            <button data-testid="softphone-close" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
          </div>

          {/* Post-call Dispose CTA — replaces the auto-opening wrap-up
              dialog. Agent reviews the CRM card + AI Copilot first, then
              clicks Dispose when ready. Soft amber banner if customer
              disconnected; soft green if agent ended.                  */}
          {pendingCdr && !sip.session && (
            <div data-testid="post-call-dispose-bar" className={`px-3 py-2 border-b flex items-center gap-2 text-xs ${customerDisconnected ? "bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200" : "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200"}`}>
              {customerDisconnected
                ? <PhoneOff className="w-3.5 h-3.5 shrink-0"/>
                : <CheckCircle2 className="w-3.5 h-3.5 shrink-0"/>}
              <span className="flex-1 font-medium">
                {customerDisconnected ? "Customer disconnected" : "Call ended"} — {pendingCdr.peer_name || pendingCdr.peer}
              </span>
              <Button size="sm" data-testid="post-call-dispose-btn" onClick={() => setWrapOpen(true)} className="h-7 px-2.5 text-[11px]">
                Dispose
              </Button>
            </div>
          )}

          {/* Body */}
          {sip.session ? (
            <LiveCallView sip={sip} busy={busy} setBusy={setBusy} conferenceActive={conferenceActive} transferConfUsed={transferConfUsed} onAgentHangup={() => { agentHangupAtRef.current = Date.now(); }} />
          ) : !active ? (
            manualDialEnabled ? (
              <DialPadView pad={pad} setPad={setPad} onCall={onCall} onSimRing={onSimRing} onWaCall={onWaCall} waCallingEnabled={waCallingEnabled} busy={busy} sipLive={sip.mode === "live" && sip.registered}
                sipMode={sip.mode} sipRegisterError={sip.registerError} sipCredError={sip.credError}
                activeCampaign={activeCampaign} />
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="softphone-manual-disabled">
                <Hash className="w-6 h-6 mx-auto mb-2 opacity-40" />
                Manual dialing is disabled for your profile.
              </div>
            )
          ) : (
            <ActiveCallView
              sp={sp} session={active} otherLine={otherLine} busy={busy} conferenceActive={conferenceActive} transferConfUsed={transferConfUsed}
              setActiveLine={setActiveLine} showDtmf={showDtmf} setShowDtmf={setShowDtmf}
              onXfer={() => window.dispatchEvent(new CustomEvent("c7:open-transfer"))}
              onConf={() => window.dispatchEvent(new CustomEvent("c7:open-conference"))}
              onVm={() => { ensureAudios(); setVmOpen(true); }}
              withBusy={withBusy}
            />
          )}

          {/* Line summary */}
          {sp.sessions.length > 0 && (
            <div className="border-t bg-muted/30 px-3 py-2 flex items-center gap-2 text-xs">
              {sp.sessions.map((s) => (
                <button
                  key={s.id}
                  data-testid={`softphone-line-${s.line_index}`}
                  onClick={() => setActiveLine(s.id)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full border ${activeLine===s.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${s.state==="talking"?"bg-emerald-500":s.state==="held"?"bg-amber-500":s.state==="ringing"?"bg-sky-500 animate-pulse":"bg-slate-400"}`}/>
                  L{s.line_index+1} · {s.state}
                </button>
              ))}
              {sp.sessions.length < 2 && active && (
                <button data-testid="softphone-add-line" onClick={()=>{ setActiveLine(null); }} className="ml-auto inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <Plus className="w-3 h-3"/>Add line
                </button>
              )}
            </div>
          )}
        </Card>
      </div>

      <BlindOrAttendedDialog open={xferOpen} onOpenChange={setXferOpen} session={active} sp={sp} />
      <ConferenceDialog open={confOpen} onOpenChange={setConfOpen} session={active} sp={sp} />
      <CallTransferControls
        dialog={transferDialog}
        onClose={() => setTransferDialog(null)}
        getCallCtx={getCallCtx}
        onWrapCdr={onTransferWrapCdr}
        onConferenceChange={(active) => {
          setConferenceActive(active);
          try { window.dispatchEvent(new CustomEvent("c7:conference-active", { detail: { active } })); } catch {}
        }}
      />
      <VoicemailDropDialog open={vmOpen} onOpenChange={setVmOpen} session={active} audios={audios} sp={sp} />
      <WrapUpDialog
        open={wrapOpen}
        onOpenChange={(v) => { setWrapOpen(v); if (!v) { setPendingCdr(null); setCustomerDisconnected(false); } }}
        cdr={pendingCdr}
        dispos={dispos}
        ensureDispos={ensureDispos}
        policy={wrapupPolicy}
        customerDisconnected={customerDisconnected}
        onSave={async (dispoId, notes, callbackPayload) => {
          const wrapSeconds = wrapOpenedAtRef.current
            ? Math.max(0, Math.floor((Date.now() - wrapOpenedAtRef.current) / 1000)) : undefined;
          if (pendingCdr) {
            const isDialerCall = !!(pendingCdr.campaign_id && pendingCdr.lead_id);
            const isCallbackCall = typeof pendingCdr.lead_id === "string"
              && pendingCdr.lead_id.startsWith("cb:");
            if (pendingCdr.is_inbound_dialer) {
              // Inbound ACD (queue) call — the CDR already exists server-side
              // (created at AgentComplete). UPDATE it with the disposition.
              try {
                await api.post("/me/inbound-cdr/disposition", {
                  cdr_id:         pendingCdr.cdr_id,
                  disposition_id: dispoId,
                  notes:          notes || "",
                  wrap_seconds:   wrapSeconds,
                });
              } catch (e) {
                try { toast.error("Failed to save inbound disposition"); } catch {}
              }
            } else if (isCallbackCall) {
              // Auto-dialed scheduled callback — write/patch its CDR so the
              // call shows in Call logs with the agent's disposition.
              try {
                await api.post(`/me/callbacks/${pendingCdr.lead_id.slice(3)}/complete`, {
                  disposition_id:   dispoId,
                  notes:            notes || "",
                  duration_seconds: pendingCdr.billable_seconds || pendingCdr.duration_seconds || 0,
                  answered:         true,
                  call_id:          pendingCdr.call_id,
                });
              } catch (e) {
                try { toast.error("Failed to log callback"); } catch {}
              }
            } else if (pendingCdr.is_live) {
              // Persist the WebRTC CDR so AHT/Calls KPIs and Call
              // History reflect reality (instead of marking every live
              // call as "missed" because no CDR existed).
              try {
                await api.post("/cdrs/live", {
                  call_id:     pendingCdr.call_id,
                  direction:   pendingCdr.direction,
                  peer:        pendingCdr.peer,
                  peer_name:   pendingCdr.peer_name,
                  started_at:  pendingCdr.started_at,
                  answered_at: pendingCdr.answered_at,
                  ended_at:    pendingCdr.ended_at || new Date().toISOString(),
                  disposition_id: dispoId,
                  wrapup_notes:   notes,
                  campaign_id:    pendingCdr.campaign_id,
                  ring_seconds:   pendingCdr.ring_seconds,
                  hold_seconds:   pendingCdr.hold_seconds,
                  talk_seconds:   pendingCdr.talk_seconds,
                  wrap_seconds:   wrapSeconds,
                  hangup_side:    pendingCdr.hangup_side,
                });
              } catch (e) { /* surface but don't block close */
                try { toast.error("Failed to record CDR — disposition saved locally."); } catch {}
              }
            } else if (!isDialerCall) {
              try { await sp.wrapup(pendingCdr.call_id, dispoId, notes); } catch {}
            }
            // Dialer/predictive call → push the disposition through the
            // dialer endpoint so the lead row + inflight ledger reflect
            // the agent-selected disposition (fixes leads stuck on
            // "dialing" / "wrap_pending").
            if (isDialerCall && !pendingCdr.is_inbound_dialer) {
              try {
                await api.post(`/dialer/campaigns/${pendingCdr.campaign_id}/disposition`, {
                  lead_id:          pendingCdr.lead_id,
                  outcome:          "answered",
                  disposition_id:   dispoId,
                  notes:            notes || "",
                  duration_seconds: pendingCdr.billable_seconds || pendingCdr.duration_seconds || 0,
                  wrap_seconds:     wrapSeconds,
                });
              } catch (e) {
                try { toast.error("Failed to update lead disposition"); } catch {}
              }
            }
            // If a callback was scheduled, persist it (any call type).
            if (callbackPayload?.scheduled_at) {
              try {
                await api.post("/me/callbacks", {
                  peer:        pendingCdr.peer,
                  peer_name:   pendingCdr.peer_name,
                  scheduled_at: callbackPayload.scheduled_at,
                  me_only:     !!callbackPayload.me_only,
                  notes:       notes,
                  disposition_id: dispoId,
                });
                try { toast.success("Callback scheduled"); } catch {}
              } catch {
                try { toast.error("Failed to schedule callback"); } catch {}
              }
            } else {
              try { toast.success("Wrapped up"); } catch {}
            }
            // Issue #3 — mirror disposition + notes into the customer's notes.
            addDispositionComment(pendingCdr, dispoId, notes);
          }
          setWrapOpen(false);
          setPendingCdr(null);
          setCustomerDisconnected(false);
          // Persist any edited CRM fields the agent left unsaved, then clear.
          try { window.dispatchEvent(new CustomEvent("c7:persist-crm")); } catch {}
          // Now safe to clear the workspace CRM popup
          try {
            window.dispatchEvent(new CustomEvent("dialer:ended", {
              detail: { source: "wrapup", disposition: dispoId },
            }));
          } catch {}
        }}
      />
    </>
  );
}

function DialPadView({ pad, setPad, onCall, onSimRing, onWaCall, waCallingEnabled, busy, sipLive, sipMode, sipRegisterError, sipCredError, activeCampaign }) {
  // Live telephony but the SIP endpoint is not registered → the phone line is
  // down (REGISTER rejected / no endpoint on Asterisk). Surface the real reason
  // instead of the misleading "Simulator mode" copy.
  const liveButOffline = sipMode === "live" && !sipLive;
  const needsCampaign = sipLive && !activeCampaign?.id;
  return (
    <div className="p-4">
      {/* Slice 16.3 — campaign comes from the workspace header (read-only here) */}
      <div className="mb-3" data-testid="manual-campaign-banner">
        <Label className="text-[11px] text-muted-foreground flex items-center gap-1.5 mb-1">
          <PhoneCall className="w-3 h-3" /> Dialing campaign
        </Label>
        {activeCampaign?.id ? (
          <div className="h-9 px-3 flex items-center justify-between rounded-md border bg-muted/40 text-sm" data-testid="manual-campaign-active">
            <span className="font-medium truncate">{activeCampaign.name}</span>
            {activeCampaign.record && <Badge variant="outline" className="text-[10px] gap-1"><Circle className="w-2 h-2 fill-rose-500 text-rose-500" /> rec</Badge>}
          </div>
        ) : (
          <div className="h-9 px-3 flex items-center rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-[11px] text-amber-700 dark:text-amber-300" data-testid="manual-campaign-hint">
            Select a campaign in the header to dial.
          </div>
        )}
      </div>
      <div className="relative">
        <Input
          data-testid="softphone-pad-input"
          value={pad}
          onChange={(e) => setPad(e.target.value.replace(/[^0-9*#+ ]/g, ""))}
          placeholder="+91 …"
          className="text-center text-2xl font-display font-bold h-14 tracking-widest"
        />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        {DTMF_KEYS.map((k) => (
          <button key={k}
            data-testid={`softphone-key-${k}`}
            onClick={() => setPad((s) => s + k)}
            className="h-11 rounded-md bg-muted hover:bg-muted/80 font-display font-bold text-lg active:scale-95 transition"
          >{k}</button>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Button data-testid="softphone-call-btn" disabled={busy || !pad || needsCampaign} onClick={() => onCall()} className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Phone className="w-4 h-4"/>}
          {sipLive ? "Call (WebRTC)" : "Call"}
        </Button>
        {!sipLive && !liveButOffline && (
          <Button data-testid="softphone-sim-ring" variant="outline" onClick={onSimRing} disabled={busy} className="gap-1.5">
            <PhoneIncoming className="w-4 h-4"/>Sim ring
          </Button>
        )}
      </div>
      {waCallingEnabled && (
        <Button data-testid="softphone-wa-call-btn" disabled={busy || !pad || needsCampaign} onClick={onWaCall} variant="outline" className="w-full mt-2 gap-2 border-[#25D366] text-[#1eb85a] hover:bg-[#25D366]/10">
          <MessageCircle className="w-4 h-4"/> WhatsApp Call
        </Button>
      )}
      <p className="text-[11px] text-muted-foreground text-center mt-3">
        {sipLive
          ? (activeCampaign?.id ? `Dialing under “${activeCampaign.name}”${activeCampaign.record ? " — recorded" : ""}.` : "WebRTC ready — pick a campaign in the header to dial.")
          : liveButOffline
          ? (sipCredError || sipRegisterError || "Phone line offline — SIP endpoint not registered. Ask your admin to run Admin → Telephony → Regenerate, then reload Asterisk.")
          : "Simulator mode — calls placed via backend state machine only."}
      </p>
    </div>
  );
}

function SplitHangup({ testId, onPlainHangup, conferenceActive }) {
  if (!conferenceActive) {
    return (
      <Button data-testid={testId} onClick={onPlainHangup} variant="destructive" className="w-full gap-1.5">
        <PhoneOff className="w-4 h-4"/>Hang up
      </Button>
    );
  }
  const fire = (scope) => { try { window.dispatchEvent(new CustomEvent("c7:hangup-leg", { detail: { scope } })); } catch {} };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid={`${testId}-split`} variant="destructive" className="w-full gap-1.5">
          <PhoneOff className="w-4 h-4"/>Hang up options<ChevronDown className="w-3.5 h-3.5 ml-auto"/>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem data-testid="hangup-customer" onClick={() => fire("customer")}>Hang up customer</DropdownMenuItem>
        <DropdownMenuItem data-testid="hangup-third" onClick={() => fire("third")}>Hang up 3rd party</DropdownMenuItem>
        <DropdownMenuItem data-testid="hangup-leave" onClick={() => fire("leave")}>Leave conference &amp; dispose</DropdownMenuItem>
        <DropdownMenuItem data-testid="hangup-all" onClick={() => fire("all")} className="text-red-600">Hang up all lines</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LiveCallView({ sip, busy, setBusy, onAgentHangup, conferenceActive, transferConfUsed }) {
  const s = sip.session;
  const ringingInbound = s.state === "ringing" && s.direction === "inbound";
  const talking = s.state === "talking";
  const held = s.held;

  async function withBusy(fn) { setBusy(true); try { await fn(); } finally { setBusy(false); } }
  function agentHangup() {
    // Route through the unified workspace hangup so BOTH the agent's own
    // WebRTC leg AND the server-side peer/customer leg are torn down — for a
    // transferred/grabbed call the SIP session can be stale and sip.terminate()
    // alone leaves the agent's call live with the customer.
    try { window.dispatchEvent(new CustomEvent("workspace:hangup")); } catch {}
    try { onAgentHangup?.(); } catch {}
    withBusy(sip.terminate);
  }

  return (
    <div className="p-4">
      <div className="text-center mb-3">
        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center justify-center gap-1.5">
          <Globe className="w-3 h-3"/>{s.direction === "outbound" ? "Outbound" : "Inbound"} · WebRTC
        </div>
        <div className="font-display font-bold text-lg truncate" data-testid="sip-peer-name">{s.peer_name || s.peer}</div>
        <div className="text-sm text-muted-foreground font-mono">{s.peer}</div>
        <div className="mt-2 flex items-center justify-center gap-2 text-xs">
          <Badge data-testid="sip-state-badge" variant={talking ? "default" : held ? "secondary" : "outline"} className="capitalize">{s.state}</Badge>
          {s.muted && <Badge variant="secondary" className="gap-1"><MicOff className="w-3 h-3"/>muted</Badge>}
          {held && <Badge variant="secondary" className="gap-1"><Pause className="w-3 h-3"/>hold</Badge>}
        </div>
      </div>

      {ringingInbound && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Button data-testid="sip-answer" onClick={() => withBusy(sip.answer)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
            <PhoneCall className="w-4 h-4"/>Answer
          </Button>
          <Button data-testid="sip-reject" onClick={() => withBusy(sip.reject)} variant="destructive" className="gap-1.5">
            <PhoneOff className="w-4 h-4"/>Reject
          </Button>
        </div>
      )}

      {!ringingInbound && talking && (
        <>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <CtlBtn testId="sip-mute" disabled={busy} active={s.muted} onClick={() => sip.setMuted(!s.muted)} icon={s.muted?MicOff:Mic} label={s.muted?"Unmute":"Mute"} />
            <CtlBtn testId="sip-hold" disabled={busy} active={s.held} onClick={() => withBusy(() => sip.setHeld(!s.held))} icon={s.held?Play:Pause} label={s.held?"Resume":"Hold"} />
            <CtlBtn testId="sip-xfer" disabled={busy || transferConfUsed} onClick={() => window.dispatchEvent(new CustomEvent("c7:open-transfer"))} icon={ArrowRightLeft} label="Transfer" title={transferConfUsed ? "Transfer/Conference already used on this call" : undefined} />
            <CtlBtn testId="sip-conf" disabled={busy || transferConfUsed} onClick={() => window.dispatchEvent(new CustomEvent("c7:open-conference"))} icon={Users2} label="Conf" title={transferConfUsed ? "Transfer/Conference already used on this call" : undefined} />
          </div>
        </>
      )}

      <SplitHangup testId="sip-hangup" conferenceActive={conferenceActive} onPlainHangup={agentHangup} />
    </div>
  );
}

function ActiveCallView({ sp, session, otherLine, busy, setActiveLine, showDtmf, setShowDtmf, onXfer, onConf, onVm, withBusy, conferenceActive, transferConfUsed }) {
  const s = session;
  const ringingInbound = s.state === "ringing" && s.direction === "inbound";
  const talking = s.state === "talking";
  const held = s.state === "held";

  return (
    <div className="p-4">
      {/* Caller card */}
      <div className="text-center mb-3">
        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
          {s.direction === "outbound" ? "Outbound" : "Inbound"} · L{s.line_index+1}
        </div>
        <div className="font-display font-bold text-lg truncate" data-testid="softphone-peer-name">{s.peer_name || s.peer}</div>
        <div className="text-sm text-muted-foreground font-mono">{s.peer}</div>
        <div className="mt-2 flex items-center justify-center gap-2 text-xs">
          <Badge variant={talking ? "default" : held ? "secondary" : "outline"} className="capitalize" data-testid="softphone-state-badge">{s.state}</Badge>
          {s.answered_at && <span className="text-muted-foreground"><CallTimer since={s.answered_at} /></span>}
          {s.recording && <Badge variant="destructive" className="gap-1"><Disc className="w-3 h-3"/>REC</Badge>}
          {s.muted && <Badge variant="secondary" className="gap-1"><MicOff className="w-3 h-3"/>muted</Badge>}
          {s.conference && <Badge variant="outline" className="gap-1"><Users2 className="w-3 h-3"/>conf · {s.parties?.length || 2}</Badge>}
        </div>
      </div>

      {/* Pre-answer buttons */}
      {ringingInbound && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Button data-testid="softphone-answer" onClick={() => withBusy(() => sp.answer(s.id))} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
            <PhoneCall className="w-4 h-4"/>Answer
          </Button>
          <Button data-testid="softphone-reject" onClick={() => withBusy(() => sp.hangup(s.id))} variant="destructive" className="gap-1.5">
            <PhoneOff className="w-4 h-4"/>Reject
          </Button>
        </div>
      )}

      {/* Control grid */}
      {!ringingInbound && (
        <>
          <div className="grid grid-cols-4 gap-2">
            <CtlBtn testId="softphone-hold" disabled={busy || s.state==="closed"} active={held} onClick={() => withBusy(() => held ? sp.unhold(s.id) : sp.hold(s.id))} icon={held?Play:Pause} label={held?"Resume":"Hold"} />
            <CtlBtn testId="softphone-mute" disabled={busy} active={s.muted} onClick={() => withBusy(() => s.muted ? sp.unmute(s.id) : sp.mute(s.id))} icon={s.muted?MicOff:Mic} label={s.muted?"Unmute":"Mute"} />
            <CtlBtn testId="softphone-rec" disabled={busy} active={s.recording} onClick={() => withBusy(() => sp.recordingToggle(s.id))} icon={Disc} label="Rec" danger={s.recording} />
            <CtlBtn testId="softphone-dtmf" disabled={busy || !(talking||held)} active={showDtmf} onClick={() => setShowDtmf(v=>!v)} icon={Hash} label="DTMF" />
            <CtlBtn testId="softphone-xfer" disabled={busy || !talking || transferConfUsed} onClick={onXfer} icon={ArrowRightLeft} label="Transfer" title={transferConfUsed ? "Transfer/Conference already used on this call" : undefined} />
            <CtlBtn testId="softphone-conf" disabled={busy || !(talking||held) || transferConfUsed} onClick={onConf} icon={Users2} label="Conf" title={transferConfUsed ? "Transfer/Conference already used on this call" : undefined} />
            <CtlBtn testId="softphone-vm" disabled={busy} onClick={onVm} icon={Voicemail} label="VM-drop" />
            <CtlBtn testId="softphone-switch" disabled={busy || !otherLine} onClick={() => otherLine && setActiveLine(otherLine.id)} icon={ArrowLeftRight} label="Switch" />
          </div>

          {/* Inline DTMF pad */}
          {showDtmf && (talking || held) && (
            <div className="mt-3 p-3 border rounded-md bg-muted/30">
              <div className="grid grid-cols-3 gap-1.5">
                {DTMF_KEYS.map((k) => (
                  <button key={k}
                    data-testid={`softphone-dtmf-${k}`}
                    onClick={() => sp.dtmf(s.id, k)}
                    className="h-9 rounded bg-card border border-border hover:bg-muted font-display font-bold active:scale-95">
                    {k}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Hangup */}
          <div className="mt-3">
            <SplitHangup testId="softphone-hangup" conferenceActive={conferenceActive} onPlainHangup={() => withBusy(() => sp.hangup(s.id))} />
          </div>
        </>
      )}
    </div>
  );
}

function CtlBtn({ testId, icon: Icon, label, onClick, disabled, active, danger, title }) {
  return (
    <button
      data-testid={testId} onClick={onClick} disabled={disabled} title={title}
      className={`flex flex-col items-center gap-1 px-1 py-2 rounded-md border text-[11px] font-medium transition
        ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-muted active:scale-95"}
        ${active ? (danger ? "bg-red-600 text-white border-red-600" : "bg-primary text-primary-foreground border-primary") : "bg-card border-border"}`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}

function BlindOrAttendedDialog({ open, onOpenChange, session, sp }) {
  const [tab, setTab] = useState("blind");
  const [tgt, setTgt] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!session) return null;
  const hasConsult = (sp.sessions || []).some((s) => s.consult_of === session.id);

  async function go() {
    if (tab === "blind") {
      if (!tgt) return;
      setBusy(true);
      try { await sp.transferBlind(session.id, tgt, name || null); onOpenChange(false); setTgt(""); setName(""); }
      finally { setBusy(false); }
    } else if (tab === "attended" && !hasConsult) {
      if (!tgt) return;
      setBusy(true);
      try { await sp.transferAttendedStart(session.id, tgt, name || null); }
      finally { setBusy(false); }
    } else {
      setBusy(true);
      try { await sp.transferAttendedComplete(session.id); onOpenChange(false); setTgt(""); setName(""); }
      finally { setBusy(false); }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer call</DialogTitle>
          <DialogDescription>Choose blind (immediate) or attended (consult first).</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="blind" data-testid="xfer-tab-blind">Blind</TabsTrigger>
            <TabsTrigger value="attended" data-testid="xfer-tab-attended">Attended</TabsTrigger>
          </TabsList>
          <TabsContent value="blind" className="space-y-3 mt-3">
            <div><Label>Target extension or number</Label>
              <Input data-testid="xfer-blind-target" value={tgt} onChange={(e)=>setTgt(e.target.value)} placeholder="e.g. 1005 or +91…" /></div>
            <div><Label>Target name (optional)</Label>
              <Input data-testid="xfer-blind-name" value={name} onChange={(e)=>setName(e.target.value)} /></div>
            <Button data-testid="xfer-blind-go" onClick={go} disabled={busy || !tgt} className="w-full">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <ArrowRightLeft className="w-4 h-4 mr-1.5"/>}Send blind
            </Button>
          </TabsContent>
          <TabsContent value="attended" className="space-y-3 mt-3">
            {!hasConsult ? (
              <>
                <div><Label>Consult target</Label>
                  <Input data-testid="xfer-att-target" value={tgt} onChange={(e)=>setTgt(e.target.value)} placeholder="ext or number" /></div>
                <div><Label>Name (optional)</Label>
                  <Input data-testid="xfer-att-name" value={name} onChange={(e)=>setName(e.target.value)} /></div>
                <Button data-testid="xfer-att-start" onClick={go} disabled={busy || !tgt} className="w-full">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Phone className="w-4 h-4 mr-1.5"/>}Start consult
                </Button>
              </>
            ) : (
              <>
                <div className="text-sm p-3 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 inline mr-1.5" />
                  Consult in progress on line 2. Press complete when ready to bridge the original caller.
                </div>
                <Button data-testid="xfer-att-complete" onClick={go} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <CheckCircle2 className="w-4 h-4 mr-1.5"/>}Complete transfer
                </Button>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ConferenceDialog({ open, onOpenChange, session, sp }) {
  const [tgt, setTgt] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!session) return null;
  async function go() {
    if (!tgt) return;
    setBusy(true);
    try { await sp.conferenceAdd(session.id, tgt, name || null); onOpenChange(false); setTgt(""); setName(""); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add to conference</DialogTitle>
          <DialogDescription>Brings a 3rd party into the active call.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Target ext / number</Label>
            <Input data-testid="conf-target" value={tgt} onChange={(e)=>setTgt(e.target.value)} placeholder="e.g. 1003" /></div>
          <div><Label>Name (optional)</Label>
            <Input data-testid="conf-name" value={name} onChange={(e)=>setName(e.target.value)} /></div>
          <Button data-testid="conf-go" onClick={go} disabled={busy || !tgt} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Users2 className="w-4 h-4 mr-1.5"/>}Add to conference
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VoicemailDropDialog({ open, onOpenChange, session, audios, sp }) {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  if (!session) return null;
  async function go() {
    if (!pick) return;
    setBusy(true);
    try { await sp.voicemailDrop(session.id, pick); onOpenChange(false); setPick(""); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Drop voicemail</DialogTitle>
          <DialogDescription>Play a pre-recorded message into the call and hang up.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Audio prompt</Label>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="vm-select"><SelectValue placeholder="Choose a prompt…"/></SelectTrigger>
              <SelectContent>
                {audios.length === 0 && <div className="p-3 text-xs text-muted-foreground">No audio prompts uploaded. Add some in Admin → Audio Prompts.</div>}
                {audios.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`vm-audio-${a.id}`}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="vm-go" onClick={go} disabled={busy || !pick} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Voicemail className="w-4 h-4 mr-1.5"/>}Drop & hang up
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WrapUpDialog({ open, onOpenChange, cdr, dispos, ensureDispos, onSave, policy, customerDisconnected }) {
  const [dispo, setDispo] = useState("");
  const [dispoSearch, setDispoSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  // Callback fields — only required when picked disposition has is_callback=true
  const [cbDate, setCbDate] = useState("");
  const [cbTime, setCbTime] = useState("");
  const [cbMeOnly, setCbMeOnly] = useState(true);
  // Countdown to auto-wrap. Reset whenever the dialog opens.
  const [remaining, setRemaining] = useState(0);
  const armedRef = useRef(false);
  // Reset the form ONLY when the dialog OPENS — never on every parent
  // re-render. The parent (Softphone) re-renders every second via its live
  // clock; this effect previously depended on `ensureDispos` (a fresh
  // function reference each render), so it re-ran every tick and wiped the
  // agent's selected disposition + notes — making it impossible to dispose
  // a call unless you were "super quick". Keying on `[open]` fixes that.
  useEffect(() => {
    if (open) {
      ensureDispos();
      setDispo("");
      setDispoSearch("");
      setNotes("");
      setCbDate("");
      setCbTime("");
      setCbMeOnly(true);
      const secs = policy?.enabled ? Math.max(0, parseInt(policy.seconds || 30, 10)) : 0;
      setRemaining(secs);
      armedRef.current = false; // don't auto-wrap until the countdown actually ticks
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Ticker — auto-wraps when remaining hits 0
  useEffect(() => {
    if (!open || !policy?.enabled || remaining <= 0) return;
    armedRef.current = true; // a real countdown is running
    const id = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(id);
  }, [open, policy?.enabled, remaining]);
  // Resolve selected disposition object (to read is_callback flag)
  const selectedDispo = useMemo(
    () => dispos.find((d) => (d.id || d.code || d.name) === dispo) || null,
    [dispos, dispo],
  );
  const isCallback = !!selectedDispo?.is_callback;
  const callbackReady = !isCallback || (cbDate && cbTime);

  // Filtered dispositions for the searchable grid (match on name + code).
  const filteredDispos = useMemo(() => {
    const q = dispoSearch.trim().toLowerCase();
    if (!q) return dispos;
    return dispos.filter((d) =>
      `${d.name || ""} ${d.code || ""}`.toLowerCase().includes(q)
    );
  }, [dispos, dispoSearch]);
  // ≤6 → 2 columns, >6 → 3 columns (auto-drops to 1 on mobile via grid-cols-1).
  const dispoGridCols = dispos.length > 6 ? "sm:grid-cols-3" : "sm:grid-cols-2";

  useEffect(() => {
    // Auto-wrap ONLY after a genuine countdown reached 0 (armedRef guards the
    // open-render race where `remaining` is briefly its stale 0 value).
    if (open && policy?.enabled && armedRef.current && remaining === 0
        && (policy?.code_disposition_id || dispos[0]?.id)) {
      armedRef.current = false;
      // Time's up — use the campaign-configured fallback disposition.
      // Auto-wrap NEVER schedules a callback (would be ambiguous).
      const autoDispoId = policy.code_disposition_id || dispos[0]?.id;
      const autoNotes = (notes || "") + (notes ? " " : "") + `[Auto-wrapped via ${policy.code_name || "default"}]`;
      setBusy(true);
      Promise.resolve(onSave(autoDispoId, autoNotes, null)).finally(() => setBusy(false));
    }
  }, [open, policy, remaining, dispos]);
  if (!cdr) return null;
  async function submit() {
    setBusy(true);
    try {
      const callbackPayload = isCallback && cbDate && cbTime
        ? { scheduled_at: new Date(`${cbDate}T${cbTime}`).toISOString(), me_only: cbMeOnly }
        : null;
      await onSave(dispo, notes, callbackPayload);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={() => { /* MANDATORY — no close via overlay/ESC */ }}>
      <DialogContent className="sm:max-w-2xl lg:max-w-3xl max-h-[92vh] flex flex-col" data-testid="wrapup-dialog" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Wrap-up — required</DialogTitle>
          <DialogDescription>
            Tag the outcome before continuing.
            {policy?.enabled && remaining > 0 && (
              <> Auto-wrap in <span className="font-mono font-bold text-amber-600 dark:text-amber-400">{remaining}s</span> as <span className="font-semibold">{policy.code_name || "default"}</span>.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-3">
          {customerDisconnected && (
            <div data-testid="wrapup-customer-disconnected" className="flex items-center gap-2 px-3 py-2 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 text-xs">
              <PhoneOff className="w-3.5 h-3.5"/> Customer disconnected — pick a disposition to release the call.
            </div>
          )}
          <div className="text-sm bg-muted/40 rounded-md p-3 border">
            <div className="font-display font-bold">{cdr.peer_name || cdr.peer}</div>
            <div className="text-xs text-muted-foreground font-mono">
              {cdr.direction} · {fmtTimer(cdr.duration_seconds)} ({fmtTimer(cdr.billable_seconds)} billable)
            </div>
          </div>
          <div><Label>Disposition <span className="text-red-500">*</span></Label>
            {dispos.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground border rounded-md mt-1.5">No dispositions configured for this campaign.</div>
            ) : (
              <>
                <div className="relative mt-1.5">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    data-testid="wrapup-dispo-search"
                    value={dispoSearch}
                    onChange={(e) => setDispoSearch(e.target.value)}
                    placeholder={`Search ${dispos.length} dispositions…`}
                    className="pl-8 h-9"
                    autoFocus
                  />
                </div>
                <div data-testid="wrapup-dispo" className={`mt-2 grid grid-cols-1 ${dispoGridCols} gap-2 pr-1`}>
                  {filteredDispos.map((d) => {
                    const val = d.id || d.code || d.name;
                    const active = dispo === val;
                    return (
                      <button
                        type="button"
                        key={val}
                        data-testid={`wrapup-dispo-${d.id || d.code}`}
                        onClick={() => setDispo(val)}
                        className={`relative text-left rounded-md border px-3 py-2.5 text-sm transition-colors ${
                          active
                            ? "border-primary bg-primary/10 ring-1 ring-primary"
                            : "border-border hover:bg-muted/60"
                        }`}
                      >
                        {active && <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-primary" />}
                        <div className="font-medium pr-5 truncate">{d.name || d.code}</div>
                        {d.is_callback && (
                          <Badge variant="secondary" className="mt-1 text-[10px] px-1.5 py-0">callback</Badge>
                        )}
                      </button>
                    );
                  })}
                  {filteredDispos.length === 0 && (
                    <div className="col-span-full p-3 text-xs text-muted-foreground text-center">No dispositions match “{dispoSearch}”.</div>
                  )}
                </div>
              </>
            )}
          </div>
          {isCallback && (
            <div data-testid="wrapup-callback-section" className="space-y-2 p-3 rounded-md border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
              <div className="text-xs font-semibold text-blue-900 dark:text-blue-200">
                Schedule callback
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[11px]">Date <span className="text-red-500">*</span></Label>
                  <Input data-testid="wrapup-cb-date" type="date" value={cbDate} onChange={(e)=>setCbDate(e.target.value)} min={new Date().toISOString().slice(0,10)} /></div>
                <div><Label className="text-[11px]">Time <span className="text-red-500">*</span></Label>
                  <Input data-testid="wrapup-cb-time" type="time" value={cbTime} onChange={(e)=>setCbTime(e.target.value)} /></div>
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer pt-1">
                <input
                  type="checkbox"
                  data-testid="wrapup-cb-meonly"
                  checked={cbMeOnly}
                  onChange={(e) => setCbMeOnly(e.target.checked)}
                  className="rounded"
                />
                <span>Assign to me only ({cbMeOnly ? "I'll dial it manually" : "any available agent — auto-dial"})</span>
              </label>
            </div>
          )}
          <div><Label>Notes</Label>
            <Textarea data-testid="wrapup-notes" rows={3} value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Short summary…" /></div>
        </div>
        <div className="shrink-0 border-t pt-3">
          <Button data-testid="wrapup-save" onClick={submit} disabled={busy || !dispo || !callbackReady} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <CheckCircle2 className="w-4 h-4 mr-1.5"/>}
            {isCallback ? "Schedule callback & close" : "Save & close"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
